import React from 'react';
import ReactDOM from 'react-dom';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { Provider } from 'react-redux';
import { library } from '@fortawesome/fontawesome-svg-core';
import { App } from './App';
import { store, persistor } from './reducers';
import {PersistGate} from "redux-persist/integration/react";
import ErrorBoundary from './Components/ErrorBoundary';
import { branding } from './config/portalBranding';
import country from './config/countryConfig';
import { languageChange } from './Components/Auth/auth.actions';
import './i18n'; // initialize i18next (side-effect import)
import { normalizeTrackingParams } from './Utils/normalizeTrackingParams';

// Polyfill for Firebase SDK + CRA4 + Node v24 compatibility
(window as any).process = (window as any).process || { env: { NODE_ENV: process.env.NODE_ENV || 'development' } };

// Must run before ReactDOM.render so HashRouter reads the already-normalized hash.
normalizeTrackingParams();

// Portal-specific favicon + title. index.html sets <title> from the
// build-time %REACT_APP_BRAND_NAME% placeholder (correct brand from first
// paint, no flash). This runtime assignment is the backstop: it keeps the
// title correct based on REACT_APP_PORTAL_COUNTRY and recovers gracefully if
// REACT_APP_BRAND_NAME was unset at build time. The favicon <link> is also
// overwritten here based on the resolved portal.
document.title = branding.pageTitle;
const faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
if (faviconLink) {
  faviconLink.href = branding.favicon;
} else {
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = branding.favicon;
  document.head.appendChild(link);
}

library.add(fas);

/**
 * Initial-language detection.
 *
 * Priority:
 *   1. A saved manual choice in localStorage("userLanguagePreference")
 *      — set by the Login/Signup dropdown handlers. Wins over everything.
 *   2. The device language (navigator.language), normalized: primary
 *      subtag, lowercased, with 'ja' mapped to the app's internal 'jp'
 *      code. Used only when it is one of this portal's supportedLanguages.
 *   3. Otherwise: leave the language null — components already fall back
 *      to 'en' via `props.language ?? 'en'`.
 *
 * Runs after redux-persist rehydration so it cannot race the persisted
 * state: if a manual choice was made it is re-confirmed; if not, the
 * device-detected value fills the (null) rehydrated language.
 */
const detectInitialLanguage = () => {
  try {
    const supported: string[] = country.supportedLanguages || ['en'];

    // 1. Saved manual preference — overrides device detection.
    const saved = localStorage.getItem('userLanguagePreference');
    if (saved && supported.includes(saved)) {
      store.dispatch(languageChange(saved));
      return;
    }

    // 2. Device language. Primary subtag, lowercased; map 'ja' -> 'jp'.
    const deviceLang = navigator.language || (navigator.languages && navigator.languages[0]);
    if (deviceLang) {
      let code = String(deviceLang).split('-')[0].toLowerCase();
      if (code === 'ja') { code = 'jp'; }
      if (supported.includes(code)) {
        store.dispatch(languageChange(code));
        return;
      }
    }
    // 3. No match — leave language as-is; `?? 'en'` fallback handles it.
  } catch (e) {
    // localStorage / navigator unavailable — fall through to the
    // existing 'en' default. Detection is best-effort, never fatal.
  }
};

// Run detection once redux-persist has finished rehydrating, so the
// dispatch is not overwritten by restored persisted state.
const unsubscribeRehydrate = persistor.subscribe(() => {
  const { bootstrapped } = persistor.getState();
  if (bootstrapped) {
    unsubscribeRehydrate();
    detectInitialLanguage();
  }
});

// Dynamically load the Verisoul SDK if configuration variables are set
const loadVerisoulSDK = () => {
  if (document.getElementById('verisoul-sdk-script')) return;

  const projectId = process.env.REACT_APP_VERISOUL_PROJECT_ID;
  const env = process.env.REACT_APP_VERISOUL_ENV || 'sandbox';

  if (!projectId) {
    console.error('[Verisoul ERROR] REACT_APP_VERISOUL_PROJECT_ID is not configured in .env. SDK will not load.');
    return;
  }

  const script = document.createElement('script');
  script.id = 'verisoul-sdk-script';
  script.async = true;
  script.src = `https://js.verisoul.ai/${env}/bundle.js`;
  script.setAttribute('verisoul-project-id', projectId);
  script.setAttribute('project_id', projectId);
  document.head.appendChild(script);
  console.log(`[Verisoul] Dynamic SDK loader: Script injected for project ${projectId} in ${env} env.`);
};

// Execute load
loadVerisoulSDK();

ReactDOM.render(
  <ErrorBoundary>
    <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <App />
        </PersistGate>
    </Provider>
  </ErrorBoundary>,
  document.getElementById('root'),
);
